import { useEffect, useRef, useState } from "react";

export default function useSidebarChildrenCache({
  api,
  selectedGroupId = "",
  expandedGroupId = "",
}) {
  const [sidebarChildrenByGroup, setSidebarChildrenByGroup] = useState({});
  const [sidebarLoadingGroupId, setSidebarLoadingGroupId] = useState("");
  const selectedGroupIdRef = useRef(selectedGroupId);
  const expandedGroupIdRef = useRef(expandedGroupId);
  const sidebarChildrenByGroupRef = useRef(sidebarChildrenByGroup);

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  useEffect(() => {
    expandedGroupIdRef.current = expandedGroupId;
  }, [expandedGroupId]);

  useEffect(() => {
    sidebarChildrenByGroupRef.current = sidebarChildrenByGroup;
  }, [sidebarChildrenByGroup]);

  const refreshSidebarChildren = async (groupId, { force = false } = {}) => {
    if (!groupId) return;
    if (!force && Object.prototype.hasOwnProperty.call(sidebarChildrenByGroupRef.current, groupId)) return;

    setSidebarLoadingGroupId(groupId);
    try {
      const res = await api.get(`/api/notes/${groupId}`);
      setSidebarChildrenByGroup((current) => ({
        ...current,
        [groupId]: res.data.entries || [],
      }));
    } catch (_err) {
      setSidebarChildrenByGroup((current) => ({
        ...current,
        [groupId]: [],
      }));
    } finally {
      setSidebarLoadingGroupId("");
    }
  };

  const invalidateSidebarChildren = (groupIds = []) => {
    const uniqueGroupIds = [...new Set((groupIds || []).filter(Boolean))];
    if (!uniqueGroupIds.length) return;

    setSidebarChildrenByGroup((current) => {
      const next = { ...current };
      for (const groupId of uniqueGroupIds) {
        delete next[groupId];
      }
      return next;
    });
  };

  const syncSidebarChildrenForGroups = async (groupIds = []) => {
    const uniqueGroupIds = [...new Set((groupIds || []).filter(Boolean))];
    for (const groupId of uniqueGroupIds) {
      const shouldRefresh =
        selectedGroupIdRef.current === groupId
        || expandedGroupIdRef.current === groupId
        || Object.prototype.hasOwnProperty.call(sidebarChildrenByGroupRef.current, groupId);

      if (shouldRefresh) {
        await refreshSidebarChildren(groupId, { force: true });
      }
    }
  };

  const resetSidebarChildrenCache = () => {
    setSidebarChildrenByGroup({});
    setSidebarLoadingGroupId("");
  };

  return {
    sidebarChildrenByGroup,
    sidebarLoadingGroupId,
    refreshSidebarChildren,
    invalidateSidebarChildren,
    syncSidebarChildrenForGroups,
    resetSidebarChildrenCache,
  };
}
